import React, { useState, useEffect, useMemo } from 'react';
import useAppStore from '../../store';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🎨 CSS-STYLES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const TableStyles = () => (
    <style>{`
        .pe-tooltip-wrapper { position: relative; }
        .pe-tooltip-wrapper:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            background-color: #E6C299; color: #000000;
            padding: 8px 12px; border-radius: 4px;
            font-size: 0.75rem; font-weight: 700;
            white-space: normal; min-width: 220px; max-width: 300px;
            z-index: 2147483647 !important; pointer-events: none;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8);
            margin-bottom: 12px; text-align: center;
            border: 2px solid #ffffff; line-height: 1.3;
        }
        .pe-tooltip-wrapper:hover::before {
            content: ''; position: absolute;
            bottom: 100%; left: 50%; margin-left: -6px;
            border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            margin-bottom: 0px; z-index: 2147483647 !important;
        }
        
        /* OPTIMIERTE SPALTENBREITEN - V1.1 FIX (Luft zum Atmen, kein Stauchen mehr!) */
        .table-wrapper {
            width: 100%;
            overflow-x: auto; /* Erlaubt horizontales Scrollen bei kleinen Monitoren */
            border-radius: 0.375rem;
            margin-bottom: 0.5rem;
            display: block;
        }
        /* Custom Scrollbar für die Tabelle */
        .table-wrapper::-webkit-scrollbar { height: 8px; }
        .table-wrapper::-webkit-scrollbar-track { background: #0f172a; border-radius: 4px; }
        .table-wrapper::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
        .table-wrapper::-webkit-scrollbar-thumb:hover { background: #64748b; }

        table { width: 100%; min-width: 1100px; border-collapse: collapse; }
        th.col-tight, td.col-tight { width: 1px; white-space: nowrap; }
        
        /* Die Eingabefelder werden breiter, die Info-Zelle schmaler */
        th.col-val,   td.col-val   { min-width: 140px; width: 140px; } 
        th.col-type,  td.col-type  { min-width: 170px; width: 170px; } 
        th.col-cat,   td.col-cat   { min-width: 260px; width: 260px; } 
        th.col-info,  td.col-info  { min-width: 200px; width: auto; }  
        
        /* STANDARD INPUT WRAPPER */
        .input-wrapper, .table-select { 
            background-color: #000000; 
            border: 1px solid #475569; 
            border-radius: 0.25rem; 
            height: 2.8rem; 
            display: flex; 
            align-items: center; 
            width: 100%;
            transition: all 0.2s;
        }
        .input-wrapper:focus-within, .table-select:focus { 
            border-color: #E6C299; 
            box-shadow: 0 0 0 1px #E6C299; 
        }

        .cost-badge { background-color: rgba(99, 102, 241, 0.1); color: #818cf8; padding: 4px 12px; border-radius: 4px; border: 1px solid #818cf8; font-weight: 700; font-size: 0.85rem; display: inline-block; text-align: center; width: 100%; }
        .cost-badge.new { background-color: rgba(16, 185, 129, 0.1); color: #34d399; border-color: #34d399; }
        .cost-badge.part { background-color: rgba(245, 158, 11, 0.1); color: #fbbf24; border-color: #fbbf24; }
        
        .input-error { border: 2px solid #ef4444 !important; background-color: rgba(239, 68, 68, 0.1); }
        
        .table-input.ghost-value { color: #64748b !important; }
        .table-input.active-value { color: #ffffff !important; font-weight: 800; }
        
        /* EFFEKT FARBEN */
        .effect-neg { color: #ef4444; font-weight: 800; } 
        .effect-pos { color: #4ade80; font-weight: 800; } 
        .effect-neutral { color: #64748b; font-weight: 400; } 
        
        option.separator { color: #64748b; font-size: 0.8rem; background-color: #0f172a; text-align: center; letter-spacing: -2px; }

        /* ADMIN BADGE STYLE */
        .admin-badge { 
            background-color: rgba(6, 78, 59, 0.9); 
            border: 1px solid #10b981; 
            color: #d1fae5; 
            font-size: 0.65rem; 
            padding: 4px 2px; 
            border-radius: 4px; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center;
            line-height: 1.1; 
            white-space: nowrap; 
            cursor: pointer; 
            transition: all 0.2s;
            width: 100%;
            height: 2.8rem;
        }
        .admin-badge:hover {
            background-color: #ef4444;
            border-color: #f87171;
            color: white;
        }
        .admin-badge:hover span { display: none; }
        .admin-badge:hover::after { content: 'UNLOCK'; font-weight: 800; }
        
        .btn-admin-lock { background-color: #1e293b; color: #64748b; border: 1px solid #334155; width: 100%; height: 2.8rem; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; border-radius: 0.25rem; font-size: 0.9rem; }
        .btn-admin-lock:hover { background-color: #334155; color: #E6C299; border-color: #E6C299; }
    `}</style>
);

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🧠 HELPER: BWA DATEN EXTRAKTION (Korrekte BWA Sortierung)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface BwaCostItem {
    id: string;
    label: string;
    monthlyValue: number;
}

const useBwaCosts = () => {
    const { clientData } = useAppStore();
    
    return useMemo(() => {
        const bwa = clientData.bwaData || {};
        const zeitraum = bwa.zeitraum || 12; 
        
        const materialItem: BwaCostItem | null = (bwa.material && bwa.material !== 0) 
            ? { id: 'material', label: 'Materialeinsatz', monthlyValue: bwa.material / zeitraum } 
            : null;

        const otherItems: BwaCostItem[] = [];

        const add = (id: string, label: string, yearlyVal: number | undefined) => {
            if (yearlyVal && yearlyVal !== 0) {
                otherItems.push({ id, label, monthlyValue: yearlyVal / zeitraum });
            }
        };

        // 1. FESTE BWA REIHENFOLGE
        add('raumkosten', 'Raumkosten', bwa.raumkosten);
        add('betriebssteuern', 'Betriebliche Steuern', bwa.betriebssteuern);
        add('versicherungen', 'Versicherungen/Beiträge', bwa.versicherungen);
        add('besondereKosten', 'Besondere Kosten', bwa.besondereKosten);
        add('kfzKosten', 'KFZ Kosten', bwa.kfzKosten);
        add('werbeReisekosten', 'Werbe/Reisekosten', bwa.werbeReisekosten);
        add('warenabgabe', 'Warenabgabe an MA', bwa.warenabgabe);
        add('reparatur', 'Reparatur/Instandhaltung', bwa.reparatur);
        add('sonstigeKosten', 'Sonstige Kosten', bwa.sonstigeKosten);
        add('kredite', 'Kredite, Zinsen u.ä.', bwa.kredite);
        add('zinsaufwendungen', 'Zinsaufwendungen', bwa.zinsaufwendungen);
        add('gewerbesteuer', 'Gewerbesteuer', bwa.gewerbesteuer);
        add('koerperschaftssteuer', 'Körperschaftssteuer (GmbH)', bwa.koerperschaftssteuer);
        
        // 2. Dynamische Geräte
        const vCount = bwa.visibleGeraeteCount ?? 4;
        for(let i=1; i<=vCount; i++) {
            const val = bwa[`geraetEintrag${i}`];
            if(val > 0) {
                const label = bwa[`geraetEintrag${i}_label`] || `Gerät ${i}`;
                if (!label.toLowerCase().includes('tool') && !label.toLowerCase().includes('calc')) {
                    add(`geraet_${i}`, label, val);
                }
            }
        }

        // 3. Sonstige Einträge
        for(let i=1; i<=4; i++) {
            const val = bwa[`sonstigeEintrag${i}`];
            if(val > 0) {
                const label = bwa[`sonstigeEintrag${i}_label`] || `Sonstiges ${i}`;
                add(`sonstiges_${i}`, label, val);
            }
        }

        return { materialItem, otherItems };
    }, [clientData.bwaData]);
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 📝 ROW COMPONENT: ACTIVE COST DRAFT
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ActiveRowProps {
    itemId: string;
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ActiveCostRow: React.FC<ActiveRowProps> = ({ itemId, onAdminLock }) => {
    const { clientData, updateCostDraft, removeCostDraft, commitDraftState } = useAppStore();
    const draft = clientData.drafts.costs?.find(i => i.id === itemId);
    const { materialItem, otherItems } = useBwaCosts();

    const fmt = (val: number | undefined) => 
        val !== undefined ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val) : '';
    
    const parse = (val: string) => parseFloat(val.replace(/\./g, '').replace(',', '.')) || 0;

    const [localValues, setLocalValues] = useState<any>(() => {
        if (!draft) return {};
        return {
            ...draft.inputs,
            prevValue: fmt(draft.inputs.prevValue),
            newValue: fmt(draft.inputs.newValue),
        };
    });

    useEffect(() => {
        if (draft) {
            setLocalValues({
                ...draft.inputs,
                prevValue: fmt(draft.inputs.prevValue),
                newValue: fmt(draft.inputs.newValue),
            });
        }
    }, [draft]);

    if (!draft) return null;

    const handleChange = (field: string, val: string) => {
        setLocalValues((prev: any) => ({ ...prev, [field]: val }));
    };

    const handleBlur = (field: string) => {
        commitDraftState();
        let val = localValues[field];

        if (['prevValue', 'newValue'].includes(field)) {
            const num = parse(val);
            updateCostDraft(itemId, field, num);
            setLocalValues((prev: any) => ({ ...prev, [field]: fmt(num) }));
        } else {
            updateCostDraft(itemId, field, val);
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLElement).blur();
        }
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const catLabel = e.target.value;
        handleChange('category', catLabel);
        updateCostDraft(itemId, 'category', catLabel);
        
        let bwaItem = otherItems.find(i => i.label === catLabel);
        if (!bwaItem && materialItem && materialItem.label === catLabel) {
            bwaItem = materialItem;
        }

        const oldVal = bwaItem ? bwaItem.monthlyValue : 0;
        updateCostDraft(itemId, 'prevValue', oldVal);
        setLocalValues((prev: any) => ({ ...prev, prevValue: fmt(oldVal) }));
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const type = e.target.value as any;
        handleChange('type', type);
        updateCostDraft(itemId, 'type', type);
        
        updateCostDraft(itemId, 'category', '');
        updateCostDraft(itemId, 'prevValue', 0);
        updateCostDraft(itemId, 'newValue', 0);
        setLocalValues((prev: any) => ({ ...prev, category: '', prevValue: '0,00', newValue: '0,00' }));
    };

    const { type, category } = localValues;
    const effect = draft.calculations.effect;
    
    // VISUELLE LOGIK: Wenn Eingabe 0 ist, zeige 0,00€ Neutral an
    const isInputZero = parse(localValues.newValue) === 0;
    const displayEffect = isInputZero ? 0 : effect;
    const isZeroEffect = Math.abs(displayEffect) < 0.01;

    const isNew = type === 'new';
    const isPart = type === 'part'; 
    const isLocked = draft.isLocked; // ADMIN STATUS
    const rowClass = `pe-row active-row ${isLocked ? 'locked-row' : ''}`;

    return (
        <tr className={rowClass}>
            {/* RESET */}
            <td className="col-tight">
                <div className="pe-tooltip-wrapper" data-tooltip="Eintrag löschen">
                    <button className="reset-btn-row" onClick={() => removeCostDraft(itemId)} disabled={isLocked}>
                        <i className="fas fa-trash-alt" style={{ color: isLocked ? '#475569' : '#ef4444', fontSize: '0.8rem' }}></i>
                    </button>
                </div>
            </td>

            {/* DATUM */}
            <td className="col-val">
                 <div className="pe-tooltip-wrapper" data-tooltip="Gültig ab (Monatserster)">
                    <select 
                        className="table-select field-date input-active-look"
                        value={localValues.date || ''}
                        onChange={(e) => { handleChange('date', e.target.value); updateCostDraft(itemId, 'date', e.target.value); }}
                        disabled={isLocked}
                    >
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* TYP */}
            <td className="col-type">
                <select 
                    className={`table-select ${!type ? 'input-error' : ''}`}
                    value={type || ''} 
                    onChange={handleTypeChange}
                    disabled={isLocked}
                >
                    <option value="" disabled>Bitte wählen...</option>
                    <option value="flat">Bestehende Pos.</option>
                    <option value="new">Neue Position</option>
                    <option value="part">Pauschale (+/-)</option>
                </select>
            </td>

            {/* KATEGORIE / NAME */}
            <td className="col-cat">
                {isNew ? (
                    <div className="pe-tooltip-wrapper" data-tooltip="Name der neuen Kostenposition">
                        <div className="input-wrapper">
                            <input 
                                type="text" 
                                className="table-input active-value text-left pl-3" 
                                placeholder="Bezeichnung..."
                                value={localValues.detail || ''}
                                onChange={(e) => handleChange('detail', e.target.value)}
                                onBlur={() => handleBlur('detail')}
                                onKeyDown={handleKeyDown}
                                disabled={isLocked}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="pe-tooltip-wrapper" data-tooltip="Welche Kostenstelle aus der BWA?">
                         <select 
                            className={`table-select text-left pl-3 ${!category ? 'input-error' : ''}`}
                            value={category || ''}
                            onChange={handleCategoryChange}
                            disabled={isLocked}
                        >
                            <option value="" disabled>Bitte wählen...</option>
                            {materialItem && (
                                <option value={materialItem.label}>{materialItem.label} ({fmt(materialItem.monthlyValue)} €)</option>
                            )}
                            {materialItem && <option disabled className="separator">──────────────────────────</option>}
                            {otherItems.map(c => (
                                <option key={c.id} value={c.label}>{c.label} ({fmt(c.monthlyValue)} €)</option>
                            ))}
                        </select>
                    </div>
                )}
            </td>

            {/* BISHERIGER WERT (GHOST) */}
            <td className="col-val">
                <div className="input-wrapper">
                    <input 
                        type="text" 
                        className="table-input ghost-value" 
                        disabled 
                        value={localValues.prevValue || ''} 
                    />
                    <div className="unit-label"><span className="unit-text">€</span></div>
                </div>
            </td>

            {/* NEUER WERT / EINGABE */}
            <td className="col-val">
                <div className="pe-tooltip-wrapper" data-tooltip={isPart ? "Differenz (+/-)" : "Neuer monatl. Gesamtwert"}>
                    <div className="input-wrapper">
                        <input 
                            type="text" 
                            className={`table-input active-value ${parse(localValues.newValue) === 0 ? 'input-error' : ''}`}
                            value={localValues.newValue || ''}
                            onChange={(e) => handleChange('newValue', e.target.value)}
                            onBlur={() => handleBlur('newValue')}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            disabled={isLocked}
                        />
                        <div className="unit-label"><span className="unit-text">€</span></div>
                    </div>
                </div>
            </td>

            {/* EFFEKT */}
            <td className="col-val">
                 <div className="result-wrapper">
                    <div className={`table-calc ${isZeroEffect ? 'effect-neutral' : (displayEffect > 0 ? 'effect-neg' : 'effect-pos')}`}>
                        {displayEffect > 0 ? '+' : ''}{fmt(displayEffect)}
                    </div>
                    <div className="unit-label"><span className="unit-text">€</span></div>
                </div>
            </td>

            {/* DETAIL / NOTIZ (AM ENDE) */}
            <td className="col-info">
                <div className="input-wrapper">
                     <input 
                        type="text" 
                        className="table-input text-left pl-3 text-sm text-pe-muted dark:text-slate-400" 
                        placeholder="Notiz / Details..."
                        value={(isNew ? localValues.notes : localValues.detail) || ''} 
                        onChange={(e) => isNew ? handleChange('notes', e.target.value) : handleChange('detail', e.target.value)}
                        onBlur={() => isNew ? handleBlur('notes') : handleBlur('detail')}
                        onKeyDown={handleKeyDown}
                        disabled={isLocked}
                    />
                </div>
            </td>

             {/* ADMIN SPERRE */}
             <td className="col-tight">
                {isLocked ? (
                    <div className="admin-badge" onClick={() => onAdminLock(itemId, true)}>
                        <span className="text-lg font-bold">{draft.lockedBy}</span>
                        <span style={{ fontSize: '0.55rem' }}>{draft.lockedAt}</span>
                    </div>
                ) : (
                    <button className="btn-admin-lock" onClick={() => onAdminLock(itemId, false)}>
                        <i className="fas fa-lock"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🃏 JOKER ROW (ADD NEW)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const JokerCostRow: React.FC = () => {
    const { addCostDraft, commitDraftState, updateCostDraft } = useAppStore();
    const [selectedDate, setSelectedDate] = useState('');

    const handleDateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        
        if (date) {
            commitDraftState();
            addCostDraft();
            
            setTimeout(() => {
                const drafts = useAppStore.getState().clientData.drafts.costs;
                if (drafts && drafts.length > 0) {
                    const lastItem = drafts[drafts.length - 1];
                    updateCostDraft(lastItem.id, 'date', date);
                }
            }, 0);
            
            setSelectedDate('');
        }
    };

    return (
        <tr className="pe-row">
            <td className="col-tight"><button className="reset-btn-row" disabled><i className="fas fa-plus text-stone-300 dark:text-slate-600"></i></button></td>
            
            {/* TRIGGER: DATE SELECT */}
            <td className="col-val">
                <div className="pe-tooltip-wrapper" data-tooltip="Datum wählen zum Hinzufügen">
                    <select 
                        className={`table-select field-date ${!selectedDate ? 'border-blue-500/50' : ''}`} 
                        value={selectedDate} 
                        onChange={handleDateSelect}
                    >
                        <option value="">Datum wählen...</option>
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* EMPTY GHOST CELLS */}
            <td className="col-type"><div className="table-select opacity-20"></div></td>
            <td className="col-cat"><div className="table-input ghost-value opacity-20">---</div></td>
            <td className="col-val"><div className="input-wrapper opacity-20"><div className="unit-label"><span className="unit-text">€</span></div></div></td>
            <td className="col-val"><div className="input-wrapper opacity-20"><div className="unit-label"><span className="unit-text">€</span></div></div></td>
            <td className="col-val"><div className="result-wrapper opacity-20"><div className="unit-label"><span className="unit-text">€</span></div></div></td>
            <td className="col-info"><div className="table-input ghost-value opacity-20">---</div></td>
            <td className="col-tight"><button className="btn-admin-lock" disabled><i className="fas fa-lock"></i></button></td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🚀 MAIN COMPONENT
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface CostsTableProps {
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const CostsTable: React.FC<CostsTableProps> = ({ onAdminLock }) => {
    const { clientData } = useAppStore();
    const costDrafts = clientData.drafts.costs || [];

    return (
        <div className="table-wrapper">
            <TableStyles />
            <table className="text-left border-collapse">
                <thead className="pe-table-header">
                    <tr>
                        <th className="col-tight px-2">Reset</th>
                        <th className="col-val px-2">Gültig ab</th>
                        <th className="col-type px-2">Art der Änderung</th>
                        <th className="col-cat px-2">Kategorie / Bezeichnung</th>
                        <th className="col-val px-2">Bisher (Monat)</th>
                        <th className="col-val px-2">Neu / Diff</th>
                        <th className="col-val px-2">Effekt (Gewinn)</th>
                        <th className="col-info px-2">Detail / Notiz</th>
                        <th className="col-tight px-2">Admin</th>
                    </tr>
                </thead>
                <tbody id="costsBody">
                    {costDrafts.map(item => (
                        <ActiveCostRow key={item.id} itemId={item.id} onAdminLock={onAdminLock} />
                    ))}
                    <JokerCostRow />
                </tbody>
            </table>
        </div>
    );
};

export default CostsTable;